import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { useModal } from "../context/ModalContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls =
  "w-full mt-2 bg-transparent border-b border-black/15 py-2 outline-none text-black placeholder:text-black/30 focus:border-[color:var(--primary-accent)] transition-colors";

export const ContactDialog = () => {
  const { contactOpen, setContactOpen } = useModal();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. Our team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    setContactOpen(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm({ name: "", email: "", company: "", message: "" });
      }, 300);
    }
  };

  return (
    <Dialog open={contactOpen} onOpenChange={close}>
      <DialogContent
        data-testid="contact-dialog"
        className="bg-white border-black/10 text-black sm:max-w-lg rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-bold tracking-tight text-black">
            {done ? "Message sent." : "Contact Sales"}
          </DialogTitle>
          <DialogDescription className="text-black/50">
            {done
              ? "Thanks for reaching out. Our team will get back to you soon."
              : "Bring next-generation crypto cards to your customers."}
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <form onSubmit={submit} className="space-y-5 mt-2">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-black/40">Name</label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-black/40">Email</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                  placeholder="jane@company.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-black/40">
                Company (optional)
              </label>
              <input
                data-testid="contact-company-input"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={inputCls}
                placeholder="Acme Fintech"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-black/40">Message</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Tell us about your use case..."
              />
            </div>
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-black text-white font-semibold py-3.5 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Contact Sales"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
